import React from "react";
import type { TStoreRoot } from "@/store/createStoreRoot";
import { convertReconciliation } from "@/store/extractionAnnualized/reconciliation";
import type { createExtractionQueryHooks } from "@/queries/extractions/useExtractionQueries";
import type { ViewExtractionAnnualizedParams } from "./createViewExtractionAnnualized";

type OperatingStatementState = ReturnType<
    ViewExtractionAnnualizedParams["useState"]
>;
type ReconciliationParams = {
    projectId: number;
    databaseId: number;
    annualizationType: OperatingStatementState["annualizationType"];
    startDate: string | null;
    endDate: string | null;
    selectedAnnualizationGroup: OperatingStatementState["selectedAnnualizationGroup"];
};

export const createUseReconciliationState =
    ({
        useRootStoreSelector,
        extractionQueryHooks,
    }: {
        useRootStoreSelector: <T>(selector: (state: TStoreRoot) => T) => T;
        extractionQueryHooks: ReturnType<typeof createExtractionQueryHooks>;
    }): ((params: ReconciliationParams) => Partial<OperatingStatementState>) =>
    ({
        projectId,
        databaseId,
        annualizationType,
        startDate,
        endDate,
        selectedAnnualizationGroup,
    }) => {
        const showReconciliation = useRootStoreSelector(
            state => state.showReconciliation,
        );
        const actualPeriods =
            extractionQueryHooks.useOperatingStatementPeriodsQuery({
                projectId,
                databaseId,
                annualizationType: "monthly_actual",
            });
        const budgetPeriods =
            extractionQueryHooks.useOperatingStatementPeriodsQuery({
                projectId,
                databaseId,
                annualizationType: "monthly_budget",
            });
        const actualYearPeriods =
            extractionQueryHooks.useOperatingStatementPeriodsQuery({
                projectId,
                databaseId,
                annualizationType: "yearly_actual",
            });
        const budgetYearPeriods =
            extractionQueryHooks.useOperatingStatementPeriodsQuery({
                projectId,
                databaseId,
                annualizationType: "yearly_budget",
            });
        const yearly = annualizationType.startsWith("yearly_");
        const reconciliationActualType =
            yearly ||
            (!actualPeriods.isPending &&
                !actualPeriods.isError &&
                actualPeriods.data?.length === 0)
                ? "yearly_actual"
                : "monthly_actual";
        const reconciliationBudgetType =
            yearly ||
            (!budgetPeriods.isPending &&
                !budgetPeriods.isError &&
                budgetPeriods.data?.length === 0)
                ? "yearly_budget"
                : "monthly_budget";
        const sourceQueries = yearly ? [] : [actualPeriods, budgetPeriods];
        const sourcesPending = sourceQueries.some(query => query.isPending);
        const sourcesError = sourceQueries.some(query => query.isError);
        const reconciliationAvailable =
            ((actualPeriods.data?.length ?? 0) > 0 ||
                (actualYearPeriods.data?.length ?? 0) > 0) &&
            ((budgetPeriods.data?.length ?? 0) > 0 ||
                (budgetYearPeriods.data?.length ?? 0) > 0);
        const group = yearly ? "year" : selectedAnnualizationGroup;
        const reconciliation = extractionQueryHooks.useOperatingStatementQuery(
            {
                projectId,
                databaseId,
                startDate,
                endDate,
                interval: { month: 1, quarter: 3, half: 6, year: 12 }[group],
                annualizationType,
                reconciliation: true,
            },
            { enabled: showReconciliation && !sourcesPending && !sourcesError },
        );
        const reconciliationData = React.useMemo(
            () =>
                showReconciliation
                    ? convertReconciliation(reconciliation.data ?? null, group)
                    : null,
            [showReconciliation, reconciliation.data, group],
        );
        if (!showReconciliation)
            return { showReconciliation, reconciliationAvailable };
        const periodQueries = [
            reconciliationActualType === "yearly_actual"
                ? actualYearPeriods
                : actualPeriods,
            reconciliationBudgetType === "yearly_budget"
                ? budgetYearPeriods
                : budgetPeriods,
        ];
        return {
            showReconciliation,
            reconciliationAvailable,
            reconciliationPeriods: reconciliationData?.reconciliationPeriods,
            availablePeriods: periodQueries.flatMap(query => query.data ?? []),
            loadAnnualizedExtractionPeriodsStatus: [
                ...sourceQueries,
                ...periodQueries,
            ].some(query => query.isPending)
                ? "pending"
                : [...sourceQueries, ...periodQueries].some(
                        query => query.isError,
                    )
                  ? "rejected"
                  : "resolved",
            loadAnnualizedExtractionStatus:
                startDate === null || endDate === null
                    ? "idle"
                    : sourcesPending
                      ? "pending"
                      : sourcesError
                        ? "rejected"
                        : reconciliation.isPending
                          ? "pending"
                          : reconciliation.isError
                            ? "rejected"
                            : "resolved",
            columnDefsOperatingStatements:
                reconciliationData?.columnDefsOperatingStatements ?? null,
            rows: reconciliationData?.rows ?? null,
            nonOperatingRows: reconciliationData?.nonOperatingRows ?? null,
            totalNetIncome: reconciliationData?.totalNetIncome ?? null,
        };
    };
